<script setup lang="ts">
import { PlusCircleIcon } from '@heroicons/vue/24/outline'
import { toTypedSchema } from '@vee-validate/zod'
import { storeToRefs } from 'pinia'
import { useForm } from 'vee-validate'
import { inject, ref, toRef, watch } from 'vue'
import { number, object, string } from 'zod'

import type { DropdownOption } from '@/components/common/ui/inputs/inputTypes'
import type { BRMOrganizationBase } from '@/types/external/2work/doctypes/CustomDoctypes'
import type { DynamicLinkCreation } from '@/types/external/2work/doctypes/generatorOutput/Core/DynamicLinkCreation'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import ButtonPrimary from '@/components/common/ui/buttons/ButtonPrimary.vue'
import ButtonSecondary from '@/components/common/ui/buttons/ButtonSecondary.vue'
import SingleSelect from '@/components/common/ui/inputs/SingleSelect.vue'
import TextInput from '@/components/common/ui/inputs/TextInput.vue'
import { personDetailContextInjectionKey } from '@/composables/providers/usePersonDetail'
import { RelationGroupType } from '@/constants/relations'
import { useCreateRelationSlideInStore } from '@/stores/createRelationSlideInStore'
import { translationFunctionCommon, translationFunctionDefault } from '@/utilities/translations'

interface AddOrganizationFormFields {
  is_primary_link: 0 | 1
  organization: BRMOrganizationBase | null
  tw_description: null | string
  tw_role: null | string
}

const props = defineProps<{
  open: boolean
  organizationOptions: DropdownOption<BRMOrganizationBase>[]
  roleOptions: DropdownOption<string>[]
  submitForm(values: DynamicLinkCreation & { image?: string | undefined }): Promise<void>
}>()

const emit = defineEmits<{
  onCancel: []
}>()

const createRelationSlideInStore = useCreateRelationSlideInStore()
const { openCreateRelationOverlay } = createRelationSlideInStore
const { initialPersonOrganisationRelationLinkName } = storeToRefs(createRelationSlideInStore)

const personDetailContext = inject(personDetailContextInjectionKey)

const openCreateOrganizationOverlay = (searchTerm?: string): void => {
  const person = personDetailContext?.person.value
  openCreateRelationOverlay(RelationGroupType.organizations, {
    prefillMetaData: {
      prefillName: searchTerm,
      ...(person
        ? {
            sourceEntityName: person.full_name,
            sourceEntityType: 'person',
            tw_responsible_employee: person.tw_responsible_employee?.name,
            ...(person.tw_rating ? { tw_rating: person.tw_rating } : {}),
          }
        : {}),
    },
  })
}

const tfCommon = translationFunctionCommon()
const tfDefault = translationFunctionDefault()

const validationSchema = toTypedSchema(
  object({
    is_primary_link: number().min(0).max(1),
    organization: object({
      doctype: string().min(1),
      image: string().optional().nullable(),
      name: string().min(1),
      organization_name: string().min(1),
    }).required(),
    tw_description: string().nullable().optional(),
    tw_role: string().min(1),
  }),
)

const { handleSubmit, resetForm } = useForm<AddOrganizationFormFields>({
  initialValues: {
    is_primary_link: 0,
    organization: null,
    tw_description: '',
    tw_role: '',
  },
  validateOnMount: false,
  validationSchema,
})

const organizationOptionsRef = toRef(props, 'organizationOptions')
const initialValue = ref<BRMOrganizationBase | undefined>()

const onSubmit = handleSubmit(
  async (values) => {
    await props
      .submitForm({
        doctype: 'Dynamic Link',
        image: values.organization?.image,
        link_doctype: values.organization?.doctype ?? '',
        link_name: values.organization?.name ?? '',
        link_title: values.organization?.organization_name ?? '',
        tw_description: values.tw_description ?? '',
        tw_is_primary_link: values.is_primary_link,
        tw_role: values.tw_role ?? '',
      })
      .then(() => {
        resetForm()
        initialValue.value = undefined
      })
  },
  ({ errors, values }) => {
    console.debug(errors)
    console.debug({ values })
  },
)

const handleClickCancel = (): void => {
  emit('onCancel')
}

watch(
  [organizationOptionsRef, initialPersonOrganisationRelationLinkName],
  ([options]) => {
    if (!initialPersonOrganisationRelationLinkName.value) {
      return
    }

    const target = options.find(
      (opt) => opt.optionValue.name === initialPersonOrganisationRelationLinkName.value,
    )

    if (target) {
      initialValue.value = target.optionValue
      initialPersonOrganisationRelationLinkName.value = undefined
    }
  },
  { immediate: true },
)
</script>

<template>
  <div
    v-if="open || initialValue"
    class="space-y-4"
    :data-testid="TEST_IDS_CREATE.addOrganizationForm.root"
  >
    <SingleSelect
      :data-testid="TEST_IDS_CREATE.addOrganizationForm.selectOrganizationRoot"
      :empty-state-action="{
        icon: PlusCircleIcon,
        labelTranslationKey: 'common:addEntity.organization',
        action: openCreateOrganizationOverlay,
      }"
      is-organization
      :label="tfDefault('editPerson.organizationName')"
      name="organization"
      on-white-bg
      :options="[organizationOptions]"
      :preselect-value="initialValue"
      required
      searchable
    />
    <SingleSelect
      :data-testid="TEST_IDS_CREATE.addOrganizationForm.selectRoleRoot"
      :label="tfDefault('editPerson.organizationRole')"
      name="tw_role"
      on-white-bg
      :options="[roleOptions]"
      required
      searchable
    />
    <TextInput
      :data-testid="TEST_IDS_CREATE.addOrganizationForm.roleDescription"
      :label="tfDefault('editPerson.organizationDescription')"
      name="tw_description"
    />
    <div class="flex justify-around">
      <ButtonPrimary
        :data-testid="TEST_IDS_CREATE.addOrganizationForm.submit"
        :label="tfDefault('editPerson.organizationButtonLabelConfirm')"
        @click="onSubmit"
      />
      <ButtonSecondary :label="tfCommon('button.cancel')" @click="handleClickCancel" />
    </div>
  </div>
</template>
